import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import {Panel, PanelGroup, PanelResizeHandle} from 'react-resizable-panels';

import {DataCaptureDraftDocument} from 'llm/data-capture/DataCaptureDraftDocument';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {GRAY, GRAY_LIGHT, hexToRgba} from 'ui-library/Colors';
import useOnMount from 'ui-library/hooks/useOnMount';

import {DraftDocument} from './DataCaptureTypes';

export interface DataCaptureSideBySideLayoutProps {
  chatContent: JSX.Element;
  draftDocument: DraftDocument;
  isDocumentEditable: boolean;
  locked: boolean;
}

export function DataCaptureSideBySideLayout({
  locked,
  chatContent,
  draftDocument,
  isDocumentEditable,
}: DataCaptureSideBySideLayoutProps): JSX.Element {
  const [isDragging, setIsDragging] = useState(false);
  useOnMount(() => {
    SidepanelActions.expand();
  });

  return (
    <div
      className={css(styles.sideBySideContainer)}
      {...getTestProps({component: 'DataCaptureSideBySideLayout', element: 'root'})}
    >
      <PanelGroup direction="horizontal" style={{width: '100%', height: '100%'}}>
        <Panel defaultSize={33} minSize={20} maxSize={80} className={css(styles.chatPanel)}>
          <div
            {...getTestProps({component: 'DataCaptureSideBySideLayout', element: 'chat-panel'})}
            className={css(styles.panelWrapper)}
          >
            <div
              className={css(styles.scrollableContent)}
              {...getTestProps({component: 'DataCaptureSideBySideLayout', element: 'scrollable-content'})}
            >
              {chatContent}
            </div>
          </div>
        </Panel>

        <PanelResizeHandle
          className={css(styles.resizer, isDragging && styles.resizerActive)}
          onDragging={setIsDragging}
        >
          <div
            {...getTestProps({component: 'DataCaptureSideBySideLayout', element: 'resizer'})}
            className={css(styles.resizerWrapper)}
          >
            <div
              className={css(styles.resizerHandle)}
              {...getTestProps({component: 'DataCaptureSideBySideLayout', element: 'resizer-handle'})}
            />
          </div>
        </PanelResizeHandle>

        <Panel defaultSize={67} minSize={20} maxSize={80} className={css(styles.documentPanel)}>
          <div
            {...getTestProps({component: 'DataCaptureSideBySideLayout', element: 'document-panel'})}
            className={css(styles.panelWrapper)}
          >
            <div
              className={css(styles.scrollableContent)}
              {...getTestProps({component: 'DataCaptureSideBySideLayout', element: 'scrollable-content'})}
            >
              <DataCaptureDraftDocument
                draftDocument={draftDocument}
                isDocumentEditable={isDocumentEditable}
                locked={locked}
              />
            </div>
          </div>
        </Panel>
      </PanelGroup>
    </div>
  );
}

const styles = StyleSheet.create({
  sideBySideContainer: {
    display: 'flex',
    minHeight: 0,
    flex: 1,
    position: 'relative',
    width: '100%',
  },
  chatPanel: {
    height: '100%',
  },
  documentPanel: {
    height: '100%',
    paddingLeft: 12,
  },
  panelWrapper: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: 'white',
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  resizer: {
    width: '12px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    position: 'relative',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s ease',
    ':hover': {
      backgroundColor: `rgba(${hexToRgba(GRAY_LIGHT, 0.3)})`,
    },
    boxShadow: `2px 0 8px 0 ${GRAY}, inset -1px 0 0 0 ${GRAY_LIGHT}`,
  },
  resizerWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  resizerActive: {
    backgroundColor: `rgba(${hexToRgba(GRAY_LIGHT, 0.5)})`,
  },
  resizerHandle: {
    width: '2px',
    height: '40px',
    backgroundColor: GRAY_LIGHT,
    borderRadius: '2px',
    transition: 'all 0.15s ease',
  },
});
